import type { InkStroke } from '../model/schema.js';
import { newId } from '../model/ids.js';
import type { ElementLookup } from './attach.js';
import { strokeBoardPoints, withBoardPoints } from './attach.js';
import { boundsIntersect, boundsOf, dist, distToPolyline } from './geometry.js';
import type { InkPoint, Pt } from './types.js';

/** Parameter interval [lo, hi] along a segment or a whole polyline. */
export type Interval = readonly [number, number];

const EMPTY: null = null;

/** Segments shorter than 1 nm (board metres) count as points (CI seed -87513171). */
const MIN_LEN_SQ = 1e-18;

function clip(lo: number, hi: number): Interval | null {
  const a = Math.max(0, lo);
  const b = Math.min(1, hi);
  return a <= b ? [a, b] : EMPTY;
}

/** t ∈ [0, 1] where `a + t·(b − a)` lies within `r` of `q`. */
export function segmentCircle(a: Pt, b: Pt, q: Pt, r: number): Interval | null {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const wx = a.x - q.x;
  const wy = a.y - q.y;
  const qa = vx * vx + vy * vy;
  const c = wx * wx + wy * wy - r * r;
  // a (nearly) zero-length segment is a point – tiny lengths would underflow below
  if (qa < MIN_LEN_SQ) return c <= 0 ? [0, 1] : EMPTY;
  const qb = 2 * (vx * wx + vy * wy);
  const disc = qb * qb - 4 * qa * c;
  if (disc < 0) return EMPTY;
  const s = Math.sqrt(disc);
  return clip((-qb - s) / (2 * qa), (-qb + s) / (2 * qa));
}

/** t range where `alpha + beta·t` ∈ [lo, hi], intersected with `range`. */
function slab(
  range: Interval,
  alpha: number,
  beta: number,
  lo: number,
  hi: number,
): Interval | null {
  if (beta === 0) return alpha >= lo && alpha <= hi ? range : EMPTY;
  const t0 = (lo - alpha) / beta;
  const t1 = (hi - alpha) / beta;
  const a = Math.max(range[0], Math.min(t0, t1));
  const b = Math.min(range[1], Math.max(t0, t1));
  return a <= b ? [a, b] : EMPTY;
}

/**
 * t ∈ [0, 1] where `a + t·(b − a)` lies within `r` of the segment `c`–`d` (inside the capsule).
 * The capsule is convex, so the set is one interval: the hull of the parts in both end circles
 * and in the rectangle between them.
 */
export function segmentCapsule(a: Pt, b: Pt, c: Pt, d: Pt, r: number): Interval | null {
  const parts: Interval[] = [];
  const pc = segmentCircle(a, b, c, r);
  if (pc) parts.push(pc);
  const pd = segmentCircle(a, b, d, r);
  if (pd) parts.push(pd);
  const len = dist(c, d);
  if (len > 0) {
    const ux = (d.x - c.x) / len;
    const uy = (d.y - c.y) / len;
    const wx = a.x - c.x;
    const wy = a.y - c.y;
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const along = slab([0, 1], wx * ux + wy * uy, vx * ux + vy * uy, 0, len);
    const rect = along && slab(along, wx * -uy + wy * ux, vx * -uy + vy * ux, -r, r);
    if (rect) parts.push(rect);
  }
  if (parts.length === 0) return EMPTY;
  return [Math.min(...parts.map((p) => p[0])), Math.max(...parts.map((p) => p[1]))];
}

/** Sorted union of intervals. */
export function mergeIntervals(intervals: readonly Interval[]): Interval[] {
  const sorted = [...intervals].sort((x, y) => x[0] - y[0]);
  const out: [number, number][] = [];
  for (const [lo, hi] of sorted) {
    const last = out[out.length - 1];
    if (last && lo <= last[1]) last[1] = Math.max(last[1], hi);
    else out.push([lo, hi]);
  }
  return out;
}

/** Parameter ranges s ∈ [0, n − 1] of the stroke polyline covered by the eraser path. */
export function erasedRanges(
  points: readonly Pt[],
  eraser: readonly Pt[],
  radius: number,
): Interval[] {
  const ranges: Interval[] = [];
  const capsules: [Pt, Pt][] =
    eraser.length === 1
      ? [[eraser[0]!, eraser[0]!]]
      : eraser.slice(1).map((p, i) => [eraser[i]!, p]);
  for (let i = 1; i < points.length; i++) {
    for (const [c, d] of capsules) {
      const hit = segmentCapsule(points[i - 1]!, points[i]!, c, d, radius);
      if (hit) ranges.push([i - 1 + hit[0], i - 1 + hit[1]]);
    }
  }
  return mergeIntervals(ranges);
}

function pointAt(points: readonly InkPoint[], s: number): InkPoint {
  const i = Math.min(points.length - 2, Math.floor(s));
  const t = s - i;
  const a = points[i]!;
  const b = points[i + 1]!;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, p: a.p + (b.p - a.p) * t };
}

/** Piece of the polyline between the parameters `s0` < `s1` (dt split proportionally). */
function piece(points: readonly InkPoint[], s0: number, s1: number, withDt: boolean): InkPoint[] {
  const params = [s0];
  for (let k = Math.floor(s0) + 1; k < s1; k++) params.push(k);
  params.push(s1);
  return params.map((s, j) => {
    const pt = pointAt(points, s);
    if (withDt) {
      const prev = j === 0 ? s : params[j - 1]!;
      pt.dt = Math.round((points[Math.ceil(s)]!.dt ?? 0) * (s - prev));
    }
    return pt;
  });
}

/** Pieces shorter than this (metres, below the storage resolution) are dropped. */
export const MIN_PIECE_LENGTH = 1e-4;

function length(points: readonly Pt[]): number {
  let l = 0;
  for (let i = 1; i < points.length; i++) l += dist(points[i - 1]!, points[i]!);
  return l;
}

/**
 * Splits a stroke centreline at the eraser path (decision J3, partial erase): the parts within
 * `radius` of the path are cut out, cut points are interpolated (position, pressure, dt).
 * Returns null when the stroke is not touched, else the remaining pieces (possibly none).
 */
export function splitStroke(
  points: readonly InkPoint[],
  eraser: readonly Pt[],
  radius: number,
): InkPoint[][] | null {
  if (points.length === 1) return distToPolyline(points[0]!, eraser) <= radius ? [] : null;
  const removed = erasedRanges(points, eraser, radius);
  if (removed.length === 0) return null;
  const withDt = points.some((p) => p.dt !== undefined);
  const end = points.length - 1;
  const kept: Interval[] = [];
  let from = 0;
  for (const [lo, hi] of removed) {
    if (lo > from) kept.push([from, lo]);
    from = hi;
  }
  if (from < end) kept.push([from, end]);
  return kept
    .map(([a, b]) => piece(points, a, b, withDt))
    .filter((p) => length(p) >= MIN_PIECE_LENGTH);
}

export interface EraseOptions {
  /** Whole-stroke eraser: touched strokes are removed completely. */
  strokeMode?: boolean;
  /** Only highlighter strokes are affected. */
  highlighterOnly?: boolean;
  /** Elements for attached strokes (e.g. `BoardData.freeform`). */
  elements?: ElementLookup;
  newId?: () => string;
}

export interface EraseResult {
  removedIds: string[];
  /** Remaining pieces as new strokes (same tool, colour, layer, attachment). */
  added: InkStroke[];
}

/**
 * Applies an eraser path (board coordinates) to strokes. The eraser only affects ink
 * (decision I5); an empty path changes nothing.
 */
export function eraseStrokes(
  strokes: readonly InkStroke[],
  eraser: readonly Pt[],
  radius: number,
  opts: EraseOptions = {},
): EraseResult {
  const result: EraseResult = { removedIds: [], added: [] };
  const eb = boundsOf(eraser);
  if (eb === null) return result;
  const elements = opts.elements ?? {};
  const makeId = opts.newId ?? (() => newId());
  for (const stroke of strokes) {
    if (opts.highlighterOnly && stroke.tool !== 'highlighter') continue;
    const points = strokeBoardPoints(stroke, elements);
    if (points === null || points.length === 0 || !boundsIntersect(boundsOf(points)!, eb, radius))
      continue;
    const pieces = splitStroke(points, eraser, radius);
    if (pieces === null) continue;
    result.removedIds.push(stroke.id);
    if (opts.strokeMode) continue;
    for (const p of pieces)
      result.added.push({ ...withBoardPoints(stroke, p, elements), id: makeId() });
  }
  return result;
}
